import { useState } from "react";
import { format } from "date-fns";
import {
  DatePicker,
  ErrorMessage,
  Flex,
  LabeledData,
  Link,
  Tag,
  Text,
  TypeaheadInput,
} from "@jsq/ds/core";
import { Colors } from "@jsq/ds/styles";
import { withClientContext } from "~/apollo/client";
import { DdqProdOpsWorkflowType } from "~/graphql/supergraphEnums";
import { useGetHistoricalDdqUploadDownloadUrlQuery } from "~/graphql/supergraphHooks";
import { SPACING } from "~/styles/variables";
import { formatShortFormDate, toCalendarDate, toISOFormat } from "~/utils/datetime";
import { toDbId } from "~/utils/graphql-helpers";
import * as styles from "./styles_vanilla.css";
import { useAssignableStaffSearch } from "./useAssignableStaffSearch";
import { useDetailsTabActions } from "./useDetailsTabActions";
import type { DdqQueueRow } from "../types";

const BackfillFileLink = ({
  arenaId,
  uploadId,
  title,
}: {
  arenaId: number;
  uploadId: string;
  title: string;
}) => {
  const { data } = useGetHistoricalDdqUploadDownloadUrlQuery({
    context: withClientContext("federation"),
    variables: { arenaId, uploadId },
  });

  if (!data) return <>{title}</>;

  return (
    <Link href={data.historicalDdqUploadDownloadUrl} shouldOpenInNewTab>
      {title}
    </Link>
  );
};

export type DetailsTabProps = {
  row: DdqQueueRow;
  isLocked: boolean;
};

export const DetailsTab = ({ row, isLocked }: DetailsTabProps) => {
  const [ownerSearch, setOwnerSearch] = useState("");
  const { options, isLoading: isLoadingOptions } = useAssignableStaffSearch(ownerSearch);
  const {
    onSetOwners,
    onSetDueDate,
    onSetSubmitByDate,
    isSettingOwners,
    isSettingDueDate,
    isSettingSubmitByDate,
    ownersError,
    dueDateError,
    submitByDateError,
  } = useDetailsTabActions(row.id);

  // The setter mutations only work for DdqRequest workflows -- the backend rejects the others.
  const isEditable = !isLocked && row.workflowType === DdqProdOpsWorkflowType.DdqRequest;
  const ownerIds = row.owners.map((owner) => owner.id);
  const addableOptions = options.filter((option) => !ownerIds.includes(String(option.id)));

  const onRemoveOwner = (ownerId: string) => {
    onSetOwners(ownerIds.filter((id) => id !== ownerId));
  };

  const onAddOwner = (ownerId: string) => {
    if (ownerIds.includes(ownerId)) return;
    onSetOwners([...ownerIds, ownerId]);
  };

  if (row.workflowType === DdqProdOpsWorkflowType.BackfillDdq) {
    return (
      <Flex
        flexDirection="column"
        gap={SPACING.MEDIUM}
        className={styles.card}
        data-testid="ddq-drawer-details-tab-panel"
      >
        <Text variant="h3">Details</Text>

        <LabeledData
          variant="row"
          label="Requested by"
          data-testid="ddq-drawer-requested-by"
          value={
            row.requestedBy ? (
              <Flex flexDirection="column" alignItems="flex-end">
                <Text asSpan variant="regular-md">
                  {row.requestedBy.fullName ?? "Unnamed user"}
                </Text>
                <Text asSpan variant="regular-sm" color={Colors.Neutral600}>
                  {row.requestedBy.emailAddress}
                </Text>
              </Flex>
            ) : (
              "-"
            )
          }
        />

        <LabeledData
          variant="row"
          label="Request created"
          data-testid="ddq-drawer-request-created"
          value={format(new Date(row.createdAt), "MMM d, yyyy")}
        />

        <LabeledData
          variant="row"
          label="File"
          data-testid="ddq-drawer-file"
          value={
            row.title ? (
              <BackfillFileLink
                arenaId={toDbId(row.arena.id)}
                uploadId={row.targetId}
                title={row.title}
              />
            ) : (
              "-"
            )
          }
        />

        <LabeledData
          variant="row"
          label="Original submit date"
          data-testid="ddq-drawer-original-submit-date"
          value={
            row.originalSubmissionDate ? formatShortFormDate(row.originalSubmissionDate) : "-"
          }
        />

        <LabeledData
          variant="muted"
          label="Additional notes"
          data-testid="ddq-drawer-additional-notes"
          value={row.additionalNotes ?? "-"}
        />
      </Flex>
    );
  }

  return (
    <Flex
      flexDirection="column"
      gap={SPACING.MEDIUM}
      className={styles.card}
      data-testid="ddq-drawer-details-tab-panel"
    >
      <Text variant="h3">Details</Text>

      <LabeledData
        variant="standard"
        flippedLabel
        label="Owners"
        data-testid="ddq-drawer-owners"
        value={
          <Flex flexDirection="column" gap={SPACING.SMALL}>
            <Flex gap={SPACING.X_SMALL} flexWrap="wrap">
              {row.owners.length === 0 ? (
                // LabeledData already wraps `value` in its own <Text> (a <p>); asSpan avoids
                // nesting a block-level <p> inside it.
                <Text asSpan variant="regular-md" color={Colors.Neutral600}>
                  No owners
                </Text>
              ) : (
                row.owners.map((owner) => (
                  <Tag
                    key={owner.id}
                    label={owner.fullName ?? "Unnamed user"}
                    onRemove={isEditable ? () => onRemoveOwner(owner.id) : undefined}
                  />
                ))
              )}
            </Flex>
            {isEditable && (
              <TypeaheadInput
                options={addableOptions}
                onDebouncedSearch={setOwnerSearch}
                onSelectOption={(option) => onAddOwner(String(option.id))}
                isLoading={isLoadingOptions || isSettingOwners}
                shouldClearOnSelect
                isFullWidth
                placeholder="Add an owner"
                data-testid="ddq-drawer-owners-picker"
              />
            )}
            {ownersError && (
              <ErrorMessage data-testid="ddq-drawer-owners-error">{ownersError}</ErrorMessage>
            )}
          </Flex>
        }
      />

      <LabeledData
        variant="standard"
        flippedLabel
        label="Due date"
        data-testid="ddq-drawer-details-due-date"
        value={
          isEditable ? (
            <Flex flexDirection="column" gap={SPACING.X_SMALL}>
              <DatePicker
                selected={row.dueAt ? toCalendarDate(row.dueAt) : null}
                onChange={(date) =>
                  date &&
                  // UTC midnight of the picked calendar date, not local midnight -- otherwise
                  // toISOString() shifts the date backward a day for any positive UTC offset.
                  onSetDueDate(
                    new Date(
                      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
                    ).toISOString(),
                  )
                }
                hasIcon
                disabled={isSettingDueDate}
                placeholderText="Select date"
                data-testid="ddq-drawer-due-date-picker"
              />
              {dueDateError && (
                <ErrorMessage data-testid="ddq-drawer-due-date-error">
                  {dueDateError}
                </ErrorMessage>
              )}
            </Flex>
          ) : row.dueAt ? (
            // dueAt is stored as UTC midnight of the picked calendar date -- read it back via
            // toCalendarDate rather than formatShortFormDate, which uses local Date fields and
            // would show the previous day in any timezone ahead of UTC.
            format(toCalendarDate(row.dueAt), "MMM d, yyyy")
          ) : (
            "-"
          )
        }
      />

      <LabeledData
        variant="standard"
        flippedLabel
        label="Submit-by date"
        data-testid="ddq-drawer-details-submit-by-date"
        value={
          isEditable ? (
            <Flex flexDirection="column" gap={SPACING.X_SMALL}>
              <DatePicker
                // submitByDate is a date-only string, which the native Date constructor (unlike
                // date-fns) parses as UTC midnight -- read it back via toCalendarDate so the
                // picker doesn't show the previous day in any timezone ahead of UTC.
                selected={row.submitByDate ? toCalendarDate(row.submitByDate) : null}
                onChange={(date) => date && onSetSubmitByDate(toISOFormat(date))}
                hasIcon
                disabled={isSettingSubmitByDate}
                placeholderText="Select date"
                data-testid="ddq-drawer-submit-by-date-picker"
              />
              {submitByDateError && (
                <ErrorMessage data-testid="ddq-drawer-submit-by-date-error">
                  {submitByDateError}
                </ErrorMessage>
              )}
            </Flex>
          ) : row.submitByDate ? (
            formatShortFormDate(row.submitByDate)
          ) : (
            "-"
          )
        }
      />

      <LabeledData
        variant="standard"
        flippedLabel
        label="Additional notes"
        data-testid="ddq-drawer-additional-notes"
        value={row.additionalNotes ?? "-"}
      />
    </Flex>
  );
};
